import React, { useState, useRef, useMemo, useCallback } from 'react';
import './FieldTypeahead.css';

const MAX_SUGGESTIONS = 15;

/**
 * FieldTypeahead - Reusable autocomplete input for field name selection.
 *
 * Displays a filtered dropdown of field suggestions while allowing free-form
 * text entry. Supports keyboard navigation, ARIA attributes for accessibility,
 * and category badges (extraction vs preceding-rule).
 *
 * This component is purely advisory — it never blocks form submission.
 */
const FieldTypeahead = ({
  value,
  onChange,
  suggestions,
  placeholder,
  id,
  className,
  disabled,
  error,
  warning,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  // Filter suggestions by case-insensitive substring match, capped at 15
  const filtered = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];
    if (!value) return suggestions.slice(0, MAX_SUGGESTIONS);
    const lower = value.toLowerCase();
    return suggestions
      .filter((s) => s.value.toLowerCase().includes(lower))
      .slice(0, MAX_SUGGESTIONS);
  }, [value, suggestions]);

  const handleSelect = useCallback(
    (item) => {
      onChange(item.value);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e) => {
      onChange(e.target.value);
      setHighlightedIndex(-1);
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [onChange, isOpen]
  );

  const handleFocus = useCallback(() => {
    if (filtered.length > 0) {
      setIsOpen(true);
    }
  }, [filtered.length]);

  const handleBlur = useCallback(() => {
    // Delay closing so mousedown on an option fires first
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || filtered.length === 0) {
        // Open dropdown on ArrowDown even if closed
        if (e.key === 'ArrowDown' && filtered.length > 0) {
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1
          );
          break;

        case 'Enter':
          if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
            e.preventDefault();
            handleSelect(filtered[highlightedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        default:
          break;
      }
    },
    [isOpen, filtered, highlightedIndex, handleSelect]
  );

  const handleOptionMouseDown = useCallback(
    (item) => {
      // Clear the blur timeout so the dropdown doesn't close before selection
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      handleSelect(item);
    },
    [handleSelect]
  );

  const listboxId = id ? `${id}-listbox` : 'field-typeahead-listbox';
  const getOptionId = (index) =>
    id ? `${id}-option-${index}` : `field-typeahead-option-${index}`;

  const inputClassName = [
    'form-control',
    error ? 'is-invalid' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field-typeahead position-relative">
      <input
        ref={inputRef}
        type="text"
        id={id}
        className={inputClassName}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={
          highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined
        }
      />
      {isOpen && filtered.length > 0 && (
        <ul
          id={listboxId}
          className="field-typeahead-dropdown list-group position-absolute w-100"
          role="listbox"
        >
          {filtered.map((item, index) => (
            <li
              key={`${item.value}-${item.category}-${item.source || index}`}
              id={getOptionId(index)}
              role="option"
              aria-selected={index === highlightedIndex}
              className={`list-group-item list-group-item-action py-1 px-2 ${
                index === highlightedIndex ? 'active' : ''
              }`}
              onMouseDown={() => handleOptionMouseDown(item)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-medium">{item.value}</span>
                <small className="ms-2">
                  {item.category === 'extraction' ? (
                    <span className="badge bg-info text-dark">
                      {item.fieldType}
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      from: {item.source}
                    </span>
                  )}
                </small>
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {warning && <small className="text-warning d-block">{warning}</small>}
    </div>
  );
};

export default FieldTypeahead;
